import { Dispatch, SetStateAction, ReactElement } from 'react';
import { createOptions } from 'options';
import { Box, Button, ButtonGroup, Switch } from '@ff-it/ui';
import {
  canSetFeeMode,
  canAppendComment,
  canAltActivitName,
  canTransparentUnits,
  canAppendPONumbers,
} from './template';
import { facets, feeModes, Template } from './types';

const facetOptions = createOptions(facets, {
  OUTLET_OR_SERVICE_CATEGORY: 'Outlet or service',
});

const feeModeOptions = createOptions(feeModes);

type TemplateProps = {
  template: Template;
  setTemplate: Dispatch<SetStateAction<Template>>;
};

export function TemplateForm({
  template: { facet, feeMode, appendComment, altActivityName, transparentUnits, appendPONumbers },
  setTemplate,
}: TemplateProps): ReactElement {
  return (
    <div className="form-inline py-2">
      <ButtonGroup>
        {facetOptions.map(({ value, label }: any) => (
          <Button
            key={value}
            variant="secondary"
            size="sm"
            active={value === facet}
            onClick={() => {
              setTemplate((template) => ({ ...template, facet: value }));
            }}
          >
            {label}
          </Button>
        ))}
      </ButtonGroup>
      {canSetFeeMode(facet) && (
        <Box ml="3" display="flex" alignItems="center" gap="2">
          <span id="fee-mode-label">Fees</span>
          <ButtonGroup aria-labelledby="fee-mode-label">
            {feeModeOptions.map(({ value, label }) => (
              <Button
                key={value}
                type="button"
                variant="secondary"
                size="sm"
                active={value === feeMode}
                aria-pressed={value === feeMode}
                onClick={() => setTemplate((template) => ({ ...template, feeMode: value }))}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}
      {canAppendPONumbers(facet) && (
        <Box ml="3" display="flex" gap="2">
          <Switch
            checked={appendPONumbers}
            onChange={() => {
              setTemplate(({ appendPONumbers, ...rest }) => ({
                ...rest,
                appendPONumbers: !appendPONumbers,
              }));
            }}
            id="append-po"
          />
          <label htmlFor="append-po">Append PO numbers</label>
        </Box>
      )}
      {canAppendComment(facet) && (
        <Box ml="3" display="flex" gap="2">
          <Switch
            checked={appendComment}
            onChange={() => {
              setTemplate(({ appendComment, ...rest }) => ({
                ...rest,
                appendComment: !appendComment,
              }));
            }}
            id="append-comment"
          />
          <label htmlFor="append-comment">Append comment</label>
        </Box>
      )}
      {canAltActivitName(facet) && (
        <Box ml="3" display="flex" gap="2">
          <Switch
            checked={altActivityName}
            onChange={() => {
              setTemplate(({ altActivityName, ...rest }) => ({
                ...rest,
                altActivityName: !altActivityName,
              }));
            }}
            id="alt-name"
          />
          <label htmlFor="alt-name">Alternative activity name</label>
        </Box>
      )}
      {canTransparentUnits(facet) && (
        <Box ml="3" display="flex" gap="2">
          <Switch
            checked={transparentUnits}
            onChange={() => {
              setTemplate(({ transparentUnits, ...rest }) => ({
                ...rest,
                transparentUnits: !transparentUnits,
              }));
            }}
            id="transp-units"
          />
          <label htmlFor="transp-units">Transparent units and quantities</label>
        </Box>
      )}
    </div>
  );
}
